import React, { useEffect, useLayoutEffect, useState } from 'react'

import PageWrapper from '@/components/PageWrapper'
import gaUtils from '@/components/GAWrapper'
import history from '@/routes/history'

import LoginForm from '../components/login-form'
import SignupForm from '../components/signup-form'
import ForgotPasswordForm from '../components/forgot-password-form'
import NotificationBar from '../components/auth-modal/notification-bar'
import { AuthAutofillStyles } from '../components/auth-modal/auth-autofill-styles'
import { AuthPageNavigationProvider } from '../context/auth-page-navigation-context'
import { AUTH_STEPS } from '../constants/auth-steps'
import { AUTH_NOTICE, AUTH_NOTICE_QUERY_LOGOUT } from '../constants/auth-notice'
import {
  AUTH_SIGNUP_PATH,
  getAuthNoticeFromSearch,
  resolvePostAuthPath,
  stripAuthNoticeFromSearch,
} from '../utils/auth-return'
import { canAccessProtectedRoute } from '../services/auth-redirect'
import useAuth from '../hooks/use-auth'
import AuthPageBackdropMedia from './auth-page-backdrop'
import {
  AuthPageBackdrop,
  AuthPageContent,
  AuthPageInner,
  AuthPageRoot,
  AuthPageScrollCenter,
  AuthPageScrollLayer,
} from './auth-page.styled'

// Pagina de autenticacion
export function AuthPage ({ location }) {
  const { isAuthenticated, isInitialized, setAuthPageStep, setAuthNotice } = useAuth()
  const [showForgotPassword, setShowForgotPassword] = useState(false)

  const isSignupRoute = location.pathname === AUTH_SIGNUP_PATH

  const activeStep = showForgotPassword
    ? AUTH_STEPS.FORGOT_PASSWORD
    : isSignupRoute
      ? AUTH_STEPS.SIGNUP_FORM
      : AUTH_STEPS.LOGIN_FORM

  useEffect(() => {
    gaUtils.handleDocumentAttributes({
      isExact: true,
      params: { slug: isSignupRoute ? 'Registrate' : 'Ingresar' },
    })
  }, [isSignupRoute])

  useEffect(() => {
    setShowForgotPassword(false)
  }, [location.pathname, location.search])

  useEffect(() => {
    const noticeParam = getAuthNoticeFromSearch(location.search)
    if (noticeParam !== AUTH_NOTICE_QUERY_LOGOUT) return

    setAuthNotice(AUTH_NOTICE.LOGOUT_SUCCESS)

    const cleanSearch = stripAuthNoticeFromSearch(location.search)
    if (cleanSearch !== location.search) {
      history.replace({
        pathname: location.pathname,
        search: cleanSearch,
      })
    }
  }, [location.pathname, location.search, setAuthNotice])

  useLayoutEffect(() => {
    setAuthPageStep(activeStep)
  }, [activeStep, setAuthPageStep])

  useEffect(() => {
    return () => setAuthPageStep(null)
  }, [setAuthPageStep])

  useLayoutEffect(() => {
    if (!isInitialized || !isAuthenticated) return
    if (!canAccessProtectedRoute({ isAuthenticated, isInitialized })) return

    const destination = resolvePostAuthPath(location.search)
    const currentPath = `${location.pathname}${location.search}`

    if (destination === currentPath) return

    history.replace(destination)
  }, [isAuthenticated, isInitialized, location.pathname, location.search])

  const renderStep = () => {
    if (showForgotPassword) return <ForgotPasswordForm />
    if (isSignupRoute) return <SignupForm />
    return <LoginForm />
  }


  useEffect(() => {
    const originalHtmlOverflow = document.documentElement.style.overflow
    const originalBodyOverflow = document.body.style.overflow

    document.documentElement.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'

    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow || ''
      document.body.style.overflow = originalBodyOverflow || ''
    }
  }, [])

  if (isInitialized && isAuthenticated) {
    return null
  }

  return (
    <PageWrapper>
      <AuthAutofillStyles />
      <AuthPageNavigationProvider
        onShowForgotPassword={() => setShowForgotPassword(true)}
        onHideForgotPassword={() => setShowForgotPassword(false)}
      >
        <AuthPageRoot>
          <AuthPageBackdrop>
            <AuthPageBackdropMedia />
          </AuthPageBackdrop>

          <AuthPageScrollLayer>
            <AuthPageScrollCenter>
              <AuthPageContent>
                <AuthPageInner>
                  <NotificationBar />
                  {renderStep()}
                </AuthPageInner>
              </AuthPageContent>
            </AuthPageScrollCenter>
          </AuthPageScrollLayer>
        </AuthPageRoot>
      </AuthPageNavigationProvider>
    </PageWrapper>
  )
}

export default AuthPage
